// src/admin/hooks/useAdminKits.js
import { useState, useEffect, useCallback } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

export default function useAdminKits() {
  const isMounted = useIsMounted();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIt = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/kits`);
      if (!res.ok) throw new Error(res.statusText);
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (err) {
      if (!isMounted.current) return;
      console.error(err);
      setData([]);
      setError(err);
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchIt();
  }, [fetchIt]);

  const createKit = useCallback(
    async (kitData) => {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/kits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kit: kitData }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const message =
          (errorData.errors && errorData.errors.join(', ')) ||
          errorData.error ||
          res.statusText;
        throw new Error(message);
      }

      const created = await res.json();
      await fetchIt();
      return created;
    },
    [fetchIt]
  );

  const updateKit = useCallback(
    async (id, kitData) => {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/kits/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ kit: kitData }),
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const message =
          (errorData.errors && errorData.errors.join(', ')) ||
          errorData.error ||
          res.statusText;
        throw new Error(message);
      }

      const updated = await res.json();
      await fetchIt();
      return updated;
    },
    [fetchIt]
  );

  const deleteKit = useCallback(
    async (id) => {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/kits/${id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        const message = errorData.error || res.statusText;
        throw new Error(message);
      }

      await fetchIt();
      return true;
    },
    [fetchIt]
  );

  return {
    data,
    loading,
    error,
    refresh: fetchIt,
    createKit,
    updateKit,
    deleteKit,
  };
}

